import React, { useRef } from 'react';
import { logError, logUsage } from '../../../services/eventLog';
import { useDispatch, useSelector } from 'react-redux';
import { useReactFlow } from '@xyflow/react';
import { showAlert, showConfirm } from '../../common/AppDialog';
import { replaceCanvas, requestFitView } from '../../../store/workflow/workflowSlice';
import { FiDownload, FiUpload, FiImage } from 'react-icons/fi';
import { FaFilePdf } from 'react-icons/fa';
import { renderCanvasPng as renderPng } from '../utils/canvasImage';
import { jsPDF } from 'jspdf';
import { useTranslation } from 'react-i18next';
import ToolbarButton from './ToolbarButton';
import WorkflowActions from './WorkflowActions';

/** 내려받은 파일이 이 화면의 캔버스라는 표시 — 다시 불러올 때 아무 JSON 이나 들어오는 것을 막는다 */
const CANVAS_FILE_KIND = 'openflow.canvas';

/** 파일명에 쓸 수 없는 문자만 걸러낸 워크플로명 (없으면 workflow-canvas) */
const fileNameOf = (name?: string) => (name || '').trim().replace(/[\/:*?"<>|]+/g, '_') || 'workflow-canvas';

interface CanvasToolbarProps {
  onSave?: () => void;
  onLoad?: (data: any) => void;
}

const CanvasToolbar: React.FC<CanvasToolbarProps> = ({ onSave, onLoad }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getNodes } = useReactFlow();
  const workflowName = useSelector((state: any) => state.workflow?.current?.name as string | undefined);
  const workflowId = useSelector((state: any) => state.workflow?.current?.id as string | undefined);
  const storeNodes = useSelector((state: any) => state.workflow?.nodes || []);
  const storeEdges = useSelector((state: any) => state.workflow?.edges || []);

  /** 캔버스 전체(보이는 범위가 아니라 노드 전부)를 PNG data URL 로 — utils/canvasImage */
  const renderCanvasPng = () => renderPng(getNodes());

  /**
   * 캔버스를 **이 컴퓨터의 파일로** 내려받는다 (서버 저장은 위쪽 「저장」 이다 — 둘은 다른 일이다).
   *
   * 예전에는 `{ message: 'Sample Workflow Data' }` 라는 자리 채움만 내려받아, 저장한 줄 알고 연 파일에
   * 아무것도 없었다(현장 지적). 지금 캔버스의 노드·연결을 그대로 적는다.
   */
  const handleSave = () => {
    if (onSave) {
      onSave();
      return;
    }
    if (storeNodes.length === 0 && storeEdges.length === 0) { showAlert(t('workflow:toolbar.empty')); return; }
    const doc = {
      kind: CANVAS_FILE_KIND,
      version: 1,
      savedAt: new Date().toISOString(),
      workflow: { id: workflowId, name: workflowName },
      canvas: { nodes: storeNodes, edges: storeEdges },
    };
    const file = `${fileNameOf(workflowName)}.json`;
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file;
    a.click();
    URL.revokeObjectURL(url);       // 큰 캔버스를 여러 번 내려받아도 메모리에 쌓이지 않게
    logUsage('canvas.export', { target: workflowName, detail: { format: 'json', nodes: storeNodes.length } });
    showAlert(t('workflow:toolbar.saved', { file }));
  };

  // Load Workflow (upload JSON)
  const handleLoadClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      let data: any;
      try {
        data = JSON.parse(event.target?.result as string);
      } catch {
        showAlert(t('workflow:toolbar.badFile'));
        return;
      }
      if (onLoad) { onLoad(data); return; }
      // 위 handleSave 가 쓴 모양, 그리고 서버 캔버스 문서({canvas:{nodes,edges}})와 날것({nodes,edges}) 모두 받는다
      const canvas = data?.canvas ?? data;
      const nodes = canvas?.nodes;
      const edges = canvas?.edges;
      if (!Array.isArray(nodes) || !Array.isArray(edges)) { showAlert(t('workflow:toolbar.badFile')); return; }
      // 지금 캔버스를 덮는 일이라 반드시 묻는다 — 서버에 저장된 내용은 「저장」 을 누르기 전까지 그대로다
      if (!(await showConfirm(t('workflow:toolbar.loadConfirm', { nodes: nodes.length }), { okText: t('workflow:toolbar.loadConfirmOk'), tone: 'warning' }))) return;
      dispatch(replaceCanvas({ nodes, edges }));      // 되돌리기 한 단계로 묶인다 (historyMiddleware 가 이 동작을 안다)
      dispatch(requestFitView());
      logUsage('canvas.import', { target: workflowName, detail: { format: 'json', nodes: nodes.length } });
      showAlert(t('workflow:toolbar.loadedFile'));
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Download PNG
  const handleDownloadImage = async () => {
    try {
      const png = await renderCanvasPng();
      if (!png) { showAlert(t('workflow:toolbar.nothingToDownload')); return; }
      const a = document.createElement('a');
      a.setAttribute('download', `${fileNameOf(workflowName)}.png`);
      a.setAttribute('href', png.dataUrl);
      a.click();
      logUsage('canvas.export', { target: workflowName, detail: { format: 'png', width: png.width, height: png.height } });
    } catch (err) {
      showAlert(t('workflow:toolbar.imageFailed'));
      logError('canvas.export', err, { target: workflowName, detail: { format: 'png' } });
      console.error(err);
    }
  };

  // Download PDF
  const handleDownloadPdf = async () => {
    try {
      const png = await renderCanvasPng();
      if (!png) { showAlert(t('workflow:toolbar.nothingToDownload')); return; }
      const pdf = new jsPDF({
        orientation: png.width >= png.height ? 'l' : 'p',
        unit: 'px',
        format: [png.width, png.height],
      });
      pdf.addImage(png.dataUrl, 'PNG', 0, 0, png.width, png.height);
      pdf.save(`${fileNameOf(workflowName)}.pdf`);
      logUsage('canvas.export', { target: workflowName, detail: { format: 'pdf' } });
    } catch (err) {
      showAlert(t('workflow:toolbar.pdfFailed'));
      logError('canvas.export', err, { target: workflowName, detail: { format: 'pdf' } });
      console.error(err);
    }
  };

  return (
    // 워크플로 동작(되돌리기~실행) 옆에 파일 동작 — 한 도구 모음, 같은 아이콘 모양. 캔버스가 좁으면 여러 줄로 접힌다
    <div className="absolute top-4 right-4 left-4 flex flex-col items-end gap-1 pointer-events-none z-10">
      <WorkflowActions>
      {/* 서버 저장(Save)과 헷갈리지 않게 파일 쪽은 '내려받기' 모양 */}
      <ToolbarButton
        onClick={handleSave}
        point="toolbar:export"
        title={t('workflow:toolbar.save')}
        icon={FiDownload}
        className="text-blue-600"
      />
      <div className="relative">
        <ToolbarButton
          onClick={handleLoadClick}
          point="toolbar:import"
          title={t('workflow:toolbar.load')}
          icon={FiUpload}
          className="text-green-600"
        />
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".json"
          onChange={handleFileChange}
          data-testid="file-input"
        />
      </div>
      <ToolbarButton
        onClick={handleDownloadImage}
        point="toolbar:image"
        title={t('workflow:toolbar.image')}
        icon={FiImage}
        className="text-blue-500"
      />
      <ToolbarButton
        onClick={handleDownloadPdf}
        point="toolbar:pdf"
        title={t('workflow:toolbar.pdf')}
        icon={FaFilePdf}
        className="text-red-500"
      />
      </WorkflowActions>
    </div>
  );
};

export default CanvasToolbar;